import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE } from 'react-router-redux';
import { getFormValues } from 'redux-form';
import moment from 'moment';

import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import intersection from 'lodash/intersection';
import uniq from 'lodash/uniq';
import get from 'lodash/get';

import http from '../../shared/http';
import intl from '../../shared/intl';
import endpoints from '../../../config/endpoints';
import { enum as newsTypes } from '../../../../../enums/news-types';
import { enum as affiliateLevels } from '../../../../../enums/affiliate-levels';
import { calculateGeoGoals, getLangsByTargetRelation } from '../shared';
import { REFETCH_ALL_DATA, actions as rootActions } from '../../root';

import { create as paginationCreate } from '../../shared/pagination';
import createPromo from '../components/promo-gallery';

const pagination = paginationCreate('offer', {
	NewsList: {
		currentPage: 1,
		perPage: 3
	},
	LandingsTable: {
		currentPage: 1,
		perPage: 5
	},
	TransitsTable: {
		currentPage: 1,
		perPage: 5
	}
});

const promo = createPromo({
	namespace: 'offer',
	stateNodeName: 'promo'
});

const OFFER_FETCH_DATA = 'offer/fetch-data-success';
const OFFER_FETCH_ERROR = 'offer/fetch-data-error';

const TOGGLE_NEWS = 'offer/toggle-news';
const SORT_BY_COLUMN = 'offer/sort-by-column';

const OFFER_ADD_SUCCESS = 'offer/add-offer-success';
const OFFER_ADD_ERROR = 'offer/add-offer-error';

const FETCH_NEWS_OFFERS_SUCCESS = 'offer/fetch-news-offers-success';
const FETCH_NEWS_OFFERS_ERROR = 'offer/fetch-news-offers-error';

const SHOW_PROGRESS_BAR = 'offer/show-progress-bar';
const TOGGLE_MODAL_GALLERY = 'offer/toggle-promo-gallery';

const TOGGLE_RULES = 'offer/toggle-rules';

const BOOST_SUCCESS = 'offer/boost-success';
const BOOST_ERROR = 'offer/boost-error';

const initialState = {
	data: null,
	news: null,
	openedNewsId: null,
	openedNewsOffers: {},
	pagination: pagination.initState,
	promo: promo.initialState,
	columnSorting: {
		landing: {
			key: null,
			direction: 'desc',
			position: 'display_priority'
		},
		transit: {
			key: null,
			direction: 'desc',
			position: 'display_priority'
		}
	},
	landingTransitColumns: {
		preview: {
			id: 'preview',
			direction: 'desc',
			title: '',
			className: ''
		},
		title: {
			id: 'title',
			direction: 'desc',
			position: 'title',
			title: 'Название',
			className: 'title'
		},
		languages: {
			id: 'languages',
			position: 'languages',
			title: 'Языки',
			className: 'languages'
		},
		cr: {
			id: 'cr',
			direction: 'desc',
			position: 'cr',
			title: 'crl',
			className: 'data-value'
		}
	},
	showModalGallery: false,
	modalGalleryIndex: 0,
	shouldShowRules: false,
	offerIdForModalRules: null,
	error: null,
	progressBar: false
};

export const actions = Object.assign({
	fetchData: props => dispatch => {
		const { params } = props;
		const { id } = params;
		const offerUrl = `${endpoints.OFFERS.LIST}/${id}`;

		http({
			url: [offerUrl, `${offerUrl}/news`],
			method: 'get',
			actions: [OFFER_FETCH_DATA, OFFER_FETCH_ERROR]
		})(dispatch);
	},

	addOffer: id => dispatch => {
		const request = http({
			url: [`${endpoints.OFFERS.LIST}/${id}/request`],
			method: 'get',
			actions: [OFFER_ADD_SUCCESS, OFFER_ADD_ERROR]
		});

		dispatch({ type: SHOW_PROGRESS_BAR });
		request(dispatch);
	},

	toggleNews: (index, id) => (dispatch, getState) => {
		const openedId = getState().offer.openedNewsOffers.id;

		if (openedId != id) {
			const request = http({
				url: [`${endpoints.NEWS.LIST}/${id}`],
				method: 'get',
				actions: [FETCH_NEWS_OFFERS_SUCCESS, FETCH_NEWS_OFFERS_ERROR]
			});
			dispatch(request).then(action => {
				dispatch({
					type: TOGGLE_NEWS,
					payload: index
				});
			});
		} else {
			dispatch({
				type: TOGGLE_NEWS,
				payload: index
			});
		}
	},

	toggleModalGallery: index => ({
		type: TOGGLE_MODAL_GALLERY,
		payload: index
	}),

	toggleRules: offerId => ({
		type: TOGGLE_RULES,
		payload: offerId
	}),

	pagination: () => dispatch => pagination.actions(dispatch),

	sortByColumn: (type, columnKey) => ({
		type: SORT_BY_COLUMN,
		payload: { type, columnKey }
	}),

	boost: (offerId, clickedLocation) => dispatch => {
		const request = http({
			url: `${endpoints.OFFERS.LIST}/${offerId}/boost`,
			method: 'put',
			data: { clickedLocation },
			addToResponse: {
				offerId, clickedLocation
			},
			actions: [BOOST_SUCCESS, BOOST_ERROR]
		});

		dispatch(rootActions.spinnerToggle());
		request(dispatch).then(() => dispatch(rootActions.spinnerToggle()));
	}
}, promo.actions);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (action.payload.pathname.indexOf('offers/') !== -1) {
				return initialState;
			}
			return state;
		},
		[OFFER_FETCH_DATA]: (state, action) => {
			const [data, news] = action.payload.map(res => res.data);
			if (isEmpty(data)) {
				return { ...state, error: true };
			}
			data && data.domains && data.domains.length &&
				(data.domains = data.domains.filter(domain => domain.is_restricted === false));
			return { ...state, data, news };
		},
		[FETCH_NEWS_OFFERS_SUCCESS]: (state, action) => {
			const [data] = action.payload.map(res => res.data);
			return { ...state, openedNewsOffers: data };
		},
		[TOGGLE_NEWS]: (state, action) => {
			return { ...state, openedNewsId: action.payload === state.openedNewsId ? null : action.payload };
		},
		[SORT_BY_COLUMN]: (state, action) => {
			const { columnSorting, landingTransitColumns } = state;
			const { type, columnKey } = action.payload;
			const sorting = columnSorting[type];

			const columnDirection = sorting && sorting.key === columnKey
				? sorting.direction === 'desc' ? 'asc' : 'desc'
				: landingTransitColumns[columnKey].direction;

			return {
				...state,
				columnSorting: {
					...state.columnSorting,
					[type]: {
						...landingTransitColumns[columnKey],
						key: columnKey,
						direction: columnDirection
					}
				}
			};
		},
		[OFFER_ADD_SUCCESS]: () => {
			return initialState;
		},
		[OFFER_FETCH_ERROR]: state => {
			return { ...state, error: true, progressBar: false };
		},
		[OFFER_ADD_ERROR]: state => {
			return { ...state, error: true, progressBar: false };
		},
		[SHOW_PROGRESS_BAR]: state => {
			return { ...state, progressBar: true };
		},
		[TOGGLE_MODAL_GALLERY]: (state, action) => {
			return {
				...state,
				showModalGallery: !state.showModalGallery,
				modalGalleryIndex: action.payload || 0
			};
		},
		[TOGGLE_RULES]: (state, action) => {
			return {
				...state,
				shouldShowRules: !state.shouldShowRules,
				offerIdForModalRules: state.shouldShowRules ? null : action.payload
			};
		},
		[BOOST_SUCCESS]: (state, action) => {
			return {
				...state,
				data: {
					...state.data,
					payouts: state.data.payouts.map(payout => {
						if (payout && payout.location_id === action.meta.clickedLocation) {
							const currentLocation = state.data.locations.find(elem => elem.id === action.meta.clickedLocation);
							payout.amount = Math.round(payout.amount * (1 + Number(currentLocation.boost_value)));
							payout.boost_till = moment().add(7, 'days').format('YYYY-MM-DD HH:mm:ss');
						}

						return payout;
					})
				}
			};
		},
		[BOOST_ERROR]: state => {
			return { ...state, error: true, progressBar: false };
		},
		[REFETCH_ALL_DATA]: () => initialState
	},
	pagination.reducers,
	promo.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

export const selectors = {
	getData: state => state.offer.data && calculateGeoGoals(state.offer.data, true),
	getManagerData: state => state.managerPopup.manager,
	getNews: state =>
		pagination.selectors.getPageData(
			state => state.offer.news,
			state => state.offer.pagination.NewsList.perPage,
			state => state.offer.pagination.NewsList.currentPage
		)(state),
	getNewsTypes: () => newsTypes,
	getOpenedNewsId: state => state.offer.openedNewsId,
	getOpenedNewsOffers: state => state.offer.openedNewsOffers.news_offers,
	getLandingTransitHeaders: state => values(state.offer.landingTransitColumns),
	getColumnSorting: state => state.offer.columnSorting,
	getLandings: state => {
		const landings = getOfferDomains('landing', state);

		return pagination.selectors.getPageData(
			state => landings,
			state => state.offer.pagination.LandingsTable.perPage,
			state => state.offer.pagination.LandingsTable.currentPage
		)(state);
	},
	getTransits: state => {
		const transits = getOfferDomains('transit', state);

		return pagination.selectors.getPageData(
			state => transits,
			state => state.offer.pagination.TransitsTable.perPage,
			state => state.offer.pagination.TransitsTable.currentPage
		)(state);
	},
	getAffiliateLevels: () => affiliateLevels,
	getModalGallery: state => ({
		showModalGallery: state.offer.showModalGallery,
		modalGalleryIndex: state.offer.modalGalleryIndex
	}),
	getOfferInfo: state => {
		const offer = state.offer.data;
		if (!offer) {
			return state;
		}
		return {
			shouldShowRules: state.offer.shouldShowRules,
			offerTitle: offer.title,
			offerId: offer.id,
			offerRules: offer.rules,
			offerAvailability: offer.available,
			offerAccessThreadId: offer.offer_access_thread_id,
			offerAccessOnRequest: offer.access_on_request
		};
	},
	getIntl: state => intl(state),
	getLists: state => state.app.lists,
	getUser: state => state.app.userData && state.app.userData.general,
	getPromoGalleryData: promo.selectors.getData,
	getOfferDomainsLanguagesList: state => {
		if (!state.offer.data || !state.offer.data.domains) return [];

		const landingLanguages = state.app.lists['landing-languages'];
		const filteredDomains = filterDomainsByLangs(state.offer.data);

		const reduceLangInit = { landing:[], transit: [] };
		const domainLanguages = filteredDomains.reduce((acc, current) => {
			const type = current.type === 'landing' ? 'landing' : 'transit';
			acc[type] = uniq([].concat(acc[type], current.languages));
			return acc;
		}, reduceLangInit);

		const createFilter = availableLanguages => entity => availableLanguages.includes(entity.id);
		const toSelectOptions = entity => ({ value: entity.id, label: entity.text});

		return {
			landing: landingLanguages.filter(createFilter(domainLanguages.landing)).map(toSelectOptions),
			transit: landingLanguages.filter(createFilter(domainLanguages.transit)).map(toSelectOptions)
		};

	},
	getError: state => state.offer.error,
	getExternalIntegration: state => get(state, 'offer.data.external_integration')
};

const sortByColumn = (sortByColumn, rows) => {
	if (!rows) return null;
	if (!sortByColumn) return rows;

	return orderBy(
		rows,
		row => {
			const value = row[sortByColumn.position];
			switch (sortByColumn.position) {
				case 'cr':
				case 'epc':
					return parseFloat(value);
				default:
					return value;
			}
		},
		sortByColumn.direction
	);
};

const filterDomainsByLangs = offerData => {
	const availableLangs = getLangsByTargetRelation(offerData);
	return offerData.domains.filter(entity => !!intersection(entity.languages, availableLangs).length);
};

const getOfferDomains = (type, state) => {
	if ( !['landing', 'transit'].includes(type) )  throw Error('Wrong argument "type"');

	const langFilterName = type === 'landing' ? 'landingLanguagesFilter' : 'transitLanguagesFilter';
	const filterLanguages = getFormValues(langFilterName)(state);

	let domains = state.offer.data && state.offer.data.domains && sortByColumn(
		state.offer.columnSorting[type],
		(filterDomainsByLangs(state.offer.data)).filter(i => i.type === type)
	);

	if (filterLanguages && filterLanguages.lang && Array.isArray(domains)) {
		domains = domains.filter(entity => {
			const { lang } = filterLanguages;
			if (!lang.value) return true;
			return entity.languages.includes(lang.value);
		});
	}

	return domains;
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/offers/single/index.js